import { useCallback, useRef } from 'react';
import type { MouseEventHandler } from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';

import { expVal, expValEquals } from '@confluence/feature-experiments';
import { usePageContentId } from '@confluence/page-context';

import { doPrefetch, getPrefetchTimingInfo } from './preloadLiveDocOnDwell.ts';

type UseCollabDraftPrefetchOptions = {
	itemId: string;
	isLive: boolean;
	isDraft: boolean;
	contentType: string;
};

/**
 * Hook that prefetches collabDraft content for live docs and drafts in the page tree
 * when the user hovers over an item for a configurable delay period.
 *
 * This is completely independent of the existing usePreloadOnDelayedHover hook.
 *
 * Gated behind:
 * - `platform_editor_collabdraft_query_improvements` (dependency — cache-first consumption path)
 * - `platform_editor_prefetch_livedoc_collabdraft` (this experiment)
 */
export const useCollabDraftPrefetch = ({
	itemId,
	isLive,
	isDraft,
	contentType,
}: UseCollabDraftPrefetchOptions): {
	isPrefetchEnabled: boolean;
	onPrefetchMouseEnter: MouseEventHandler<HTMLElement>;
	onPrefetchMouseLeave: () => void;
	onPrefetchMouseDown: () => void;
	onPrefetchFocus: () => void;
	onPrefetchBlur: () => void;
} => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const [currentContentId] = usePageContentId();

	const sendPrefetchAnalyticsEvent = useCallback(
		(action: string, contentId: string, attributes: Record<string, unknown>) => {
			createAnalyticsEvent({
				type: 'sendTrackEvent',
				data: {
					action,
					actionSubject: 'pageTreeCollabDraftPrefetch',
					actionSubjectId: contentId,
					source: 'pageTree',
					attributes,
				},
			}).fire();
		},
		[createAnalyticsEvent],
	);

	// Only check experiments if we're on a live doc or draft to avoid unnecessary exposure calls.
	// Also skip if this is the current page — no point prefetching what we're already on.
	const isCurrentPage = itemId === currentContentId;
	const couldPrefetch = (isLive || isDraft) && !isCurrentPage;
	const isPrefetchEnabled =
		couldPrefetch &&
		expValEquals('platform_editor_collabdraft_query_improvements', 'isEnabled', true) &&
		expValEquals('platform_editor_prefetch_livedoc_collabdraft', 'isEnabled', true);

	const prefetchTriggeredRef = useRef<boolean>(false);
	const prefetchTimeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
	const interactionTimeRef = useRef<number>(0);
	const prefetchIdRef = useRef<string | null>(null);

	// Generate a unique prefetch ID to link triggered/unused/click events together
	const generatePrefetchId = useCallback(
		() => `${itemId}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
		[itemId],
	);

	const onPrefetchMouseEnter = useCallback<MouseEventHandler<HTMLElement>>(() => {
		if (!isPrefetchEnabled) {
			return;
		}

		// Skip if we recently prefetched this item (cache is still fresh)
		const existingPrefetch = getPrefetchTimingInfo(itemId);
		if (existingPrefetch.status === 'ready' && !existingPrefetch.cacheStale) {
			return;
		}

		prefetchTriggeredRef.current = false;
		interactionTimeRef.current = performance.now();

		prefetchTimeoutRef.current = setTimeout(
			() => {
				prefetchTriggeredRef.current = true;
				prefetchIdRef.current = generatePrefetchId();
				doPrefetch(itemId);

				sendPrefetchAnalyticsEvent('prefetchTriggered', itemId, {
					prefetchId: prefetchIdRef.current,
					isLive,
					isDraft,
					contentType,
					prefetchDelayMs: expVal('platform_editor_prefetch_livedoc_collabdraft', 'delayMs', 200),
					dwellTimeMs: performance.now() - interactionTimeRef.current,
				});
			},
			expVal('platform_editor_prefetch_livedoc_collabdraft', 'delayMs', 200),
		);
	}, [
		isPrefetchEnabled,
		itemId,
		sendPrefetchAnalyticsEvent,
		isLive,
		isDraft,
		contentType,
		generatePrefetchId,
	]);

	const onPrefetchMouseLeave = useCallback((): void => {
		// Cancel prefetch timer if user leaves before it fires
		if (prefetchTimeoutRef.current) {
			clearTimeout(prefetchTimeoutRef.current);
			prefetchTimeoutRef.current = undefined;
		}

		// Track unused prefetches — prefetch was triggered but user left without clicking
		if (prefetchTriggeredRef.current) {
			const prefetchInfo = getPrefetchTimingInfo(itemId);
			sendPrefetchAnalyticsEvent('prefetchUnused', itemId, {
				prefetchId: prefetchIdRef.current,
				isLive,
				isDraft,
				contentType,
				prefetchDelayMs: expVal('platform_editor_prefetch_livedoc_collabdraft', 'delayMs', 200),
				dwellTimeMs: performance.now() - interactionTimeRef.current,
				prefetchStatus: prefetchInfo.status,
				prefetchDurationMs: prefetchInfo.prefetchDurationMs,
			});
			prefetchTriggeredRef.current = false;
			prefetchIdRef.current = null;
		}
	}, [itemId, sendPrefetchAnalyticsEvent, isLive, isDraft, contentType]);

	const onPrefetchMouseDown = useCallback((): void => {
		// Reset triggered flag so onMouseLeave doesn't fire prefetchUnused after navigation
		prefetchTriggeredRef.current = false;

		// Cancel prefetch timer on click (we're navigating now)
		if (prefetchTimeoutRef.current) {
			clearTimeout(prefetchTimeoutRef.current);
			prefetchTimeoutRef.current = undefined;
		}

		if (!isPrefetchEnabled) {
			return;
		}

		// Get prefetch timing info for analytics
		const prefetchInfo = getPrefetchTimingInfo(itemId);

		sendPrefetchAnalyticsEvent('prefetchAndClick', itemId, {
			prefetchId: prefetchIdRef.current,
			isLive,
			isDraft,
			contentType,
			prefetchDelayMs: expVal('platform_editor_prefetch_livedoc_collabdraft', 'delayMs', 200),
			clickAfter: performance.now() - interactionTimeRef.current,
			prefetchStatus: prefetchInfo.status,
			prefetchDurationMs: prefetchInfo.prefetchDurationMs,
			prefetchTimeSinceMs: prefetchInfo.timeSincePrefetchMs,
			prefetchHeadStartMs: prefetchInfo.headStartMs,
			prefetchCacheStale: prefetchInfo.cacheStale,
		});
		prefetchIdRef.current = null;
	}, [isPrefetchEnabled, itemId, sendPrefetchAnalyticsEvent, isLive, isDraft, contentType]);

	// Focus handler — equivalent to mouseEnter for keyboard users
	const onPrefetchFocus = useCallback((): void => {
		if (!isPrefetchEnabled) {
			return;
		}

		// Skip if we recently prefetched this item (cache is still fresh)
		const existingPrefetch = getPrefetchTimingInfo(itemId);
		if (existingPrefetch.status === 'ready' && !existingPrefetch.cacheStale) {
			return;
		}

		interactionTimeRef.current = performance.now();
		prefetchTriggeredRef.current = false;

		prefetchTimeoutRef.current = setTimeout(
			() => {
				prefetchTriggeredRef.current = true;
				prefetchIdRef.current = generatePrefetchId();
				doPrefetch(itemId);
				sendPrefetchAnalyticsEvent('prefetchTriggered', itemId, {
					prefetchId: prefetchIdRef.current,
					isLive,
					isDraft,
					contentType,
					prefetchDelayMs: expVal('platform_editor_prefetch_livedoc_collabdraft', 'delayMs', 200),
					dwellTimeMs: performance.now() - interactionTimeRef.current,
					trigger: 'focus',
				});
			},
			expVal('platform_editor_prefetch_livedoc_collabdraft', 'delayMs', 200),
		);
	}, [
		isPrefetchEnabled,
		itemId,
		sendPrefetchAnalyticsEvent,
		isLive,
		isDraft,
		contentType,
		generatePrefetchId,
	]);

	// Blur handler — equivalent to mouseLeave for keyboard users
	const onPrefetchBlur = useCallback((): void => {
		// Cancel prefetch timer if user leaves before it fires
		if (prefetchTimeoutRef.current) {
			clearTimeout(prefetchTimeoutRef.current);
			prefetchTimeoutRef.current = undefined;
		}

		// Track unused prefetches
		if (prefetchTriggeredRef.current) {
			const prefetchInfo = getPrefetchTimingInfo(itemId);
			sendPrefetchAnalyticsEvent('prefetchUnused', itemId, {
				prefetchId: prefetchIdRef.current,
				isLive,
				isDraft,
				contentType,
				prefetchDelayMs: expVal('platform_editor_prefetch_livedoc_collabdraft', 'delayMs', 200),
				dwellTimeMs: performance.now() - interactionTimeRef.current,
				prefetchStatus: prefetchInfo.status,
				prefetchDurationMs: prefetchInfo.prefetchDurationMs,
				trigger: 'blur',
			});
			prefetchTriggeredRef.current = false;
			prefetchIdRef.current = null;
		}
	}, [itemId, sendPrefetchAnalyticsEvent, isLive, isDraft, contentType]);

	return {
		isPrefetchEnabled,
		onPrefetchMouseEnter,
		onPrefetchMouseLeave,
		onPrefetchMouseDown,
		onPrefetchFocus,
		onPrefetchBlur,
	};
};
